import React, { useRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import UserBrokerItem from 'components/features/security-accounts/UserBrokerItem';
import GoMoneytreeSiteDialog from 'components/features/MoveMoneytreeSiteDialog';
import Loading from 'components/commons/loading/Loading';

import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';

import * as env from 'config/environment';

import * as consts from 'config/consts';
import * as types from 'config/types';

import { useLoading } from 'hooks/useLoadign';

import {
  getUserBroker,
  GetUserBrokerResultItem,
  GetPositionsResultItem,
  deleteAccount,
  deleteManualAsset,
  getPositions,
  updateAccountAvailable,
  prepareUserBroker,
  syncAccountAsset,
  GetAccountHistoryResult,
  getAccountHistory,
} from 'config/api';
import {
  getGroup,
  useGrouping,
  isLoaded,
  isCategorized,
} from 'hooks/useGrouping';
import { useEffectMounted } from 'hooks/useEffectMounted';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import { useModal } from 'hooks/useModal';
import { useRouting } from 'hooks/useRouting';
import AccountDeleteConfirmModal from 'components/features/modal/AccountDeleteConfirmModal';
import SyncAccountAssetResultModal from 'components/features/modal/SyncAccountAssetResultModal';
import Modal from 'components/features/modal/Modal';
import { AboutExchangeRateModalProvider } from 'components/features/modal/AboutExchangeRateModal';
import { convertToMonoBrokerId } from 'components/features/utils/converter';
import { DataLinkWithdrawalAccountModal } from 'components/features/modal/DataLinkWithdrawalAccountModal';
import { WaitUntilNextUpdateModal } from 'components/features/modal/WaitUntilNextUpdateModalModal';

export type AccountHistoryProps = {
  totalAssetHistories: {
    day: number;
    totalAssetAmount: number;
  }[];
  debtAssetHistories: {
    day: number;
    debtAmount: number;
  }[];
};

const AccountDetail: React.FC = () => {
  const { account } = useParams<{ account: string }>();

  const routing = useRouting();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [showGoMoneytreeSiteDialog, setShowGoMoneytreeSiteDialog] = useState<
    boolean
  >();
  const [
    showSyncAccountAssetResultModal,
    setShowSyncAccountAssetResultModal,
  ] = useState<boolean>(false);
  const [syncAccountAssetResult, setSyncAccountAssetResult] = useState<number>(
    6
  );
  const [positions, setPositions] = useState(
    new Array<GetPositionsResultItem>()
  );
  const [brokerItem, setBrokerItem] = useState<{
    current: GetUserBrokerResultItem;
    arr: GetUserBrokerResultItem[];
  } | null>();
  const [broker, setBrokers] = useState<JSX.Element>();
  /*
    HACK: useLoadingと統合する
    統合するには、画面全体をマスクする分岐が必要
    全体に影響が出るので、時間があるときに！
  */
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isLoadingModal, setLoadingModal] = useState<boolean>(false);

  const unmounted = useRef<boolean>(false);

  const { setAction, setActionPattern } = useCustomizeHeaderContext();

  const [, { disableLoading }] = useLoading(true);

  const [groupingState, { getGroupingAPI }] = useGrouping();

  const [
    itemToUpdate,
    setItemToUpdate,
  ] = useState<GetUserBrokerResultItem | null>(null);

  const [
    isShowDataLinkWithdrawalAccountModal,
    {
      hideModal: hideDataLinkWithdrawalAccountModal,
      showModal: showDataLinkWithdrawalAccountModal,
    },
  ] = useModal();

  const [
    isShowWaitUntilNextUpdateModal,
    {
      hideModal: hideWaitUntilNextUpdateModal,
      showModal: showWaitUntilNextUpdateModal,
    },
  ] = useModal();

  const setUserBroker = async () => {
    return getUserBroker().then(ret => {
      if (!(!unmounted.current && ret)) return;
      const current = ret.Items.find(b => b.Account === account);
      if (current) setBrokerItem({ current, arr: ret.Items });
    });
  };

  const setUserPositions = (positions: Array<GetPositionsResultItem>): void => {
    setPositions(
      positions.filter(
        p =>
          p.Account === account && p.ProductType !== consts.productTypes.その他
      )
    );
  };

  const onRefreshMoneytree = (): void => {
    setUserBroker();
    getPositions().then(ret => {
      ret && setUserPositions(ret.Items);
    });
  };

  const [
    accountHistory,
    setAccountHistory,
  ] = useState<AccountHistoryProps | null>(null);

  useEffect(() => {
    (async () => {
      getGroupingAPI();

      const result: GetAccountHistoryResult = await getAccountHistory(account);
      const data = {
        totalAssetHistories: result.TotalAssetHistories.map(ret => {
          return {
            day: ret.Day,
            totalAssetAmount: ret.TotalAssetAmount,
          };
        }),
        debtAssetHistories: result.DebtAssetHistories.map(ret => {
          return {
            day: ret.Day,
            debtAmount: ret.DebtAmount,
          };
        }),
      } as AccountHistoryProps;
      setAccountHistory(data);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getPositions().then(ret => {
      !unmounted.current && ret && setUserPositions(ret.Items);
    });
    setUserBroker();

    return (): void => {
      // unmount
      unmounted.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  useEffect(() => {
    broker && positions && accountHistory && disableLoading();
  }, [broker, positions, accountHistory, disableLoading]);

  useEffectMounted(() => {
    routing.finishNormalRouting();
  });

  const group = brokerItem
    ? getGroup(brokerItem.current.Account, groupingState)
    : null;

  useCustomHeaderTitle(brokerItem ? brokerItem.current.BrokerName : '');
  useEffect(
    () => {
      setAction(() => (type: types.HeaderActionType): void => {
        switch (type) {
          case consts.HeaderActionType.編集:
            doChange();
            break;
          case consts.HeaderActionType.削除:
            doDeleteConfirm();
            break;
        }
      });

      setActionPattern(
        !brokerItem
          ? consts.HeaderActionPatternType.なし
          : brokerItem.current.AggregateSource ===
            consts.AggregateSourceTypes.マネーツリー
          ? consts.HeaderActionPatternType.編集_削除_1つ
          : consts.HeaderActionPatternType.編集_削除_それぞれ
      );

      const doChange = (): void => {
        if (!brokerItem) return;

        switch (brokerItem.current.AggregateSource) {
          case consts.AggregateSourceTypes.テコテック:
            doChangeCritter(brokerItem.current);
            break;

          case consts.AggregateSourceTypes.MONO:
            doChangeMono(brokerItem.current);
            // TODO: MONOの場合の口座詳細画面の編集ボタンを押した際の挙動
            break;
          case consts.AggregateSourceTypes.マネーツリー:
            setShowGoMoneytreeSiteDialog(true);
            break;

          case consts.AggregateSourceTypes.手入力:
            routing.pushRouting(
              { path: `/optionalassetrenewal/${brokerItem.current.Account}` },
              true
            );
            break;
        }
      };

      // テコテックの編集は、MONOの新規登録に遷移させる
      // ただし、並行稼働完了後に破棄
      const doChangeCritter = async (
        brokerItem: GetUserBrokerResultItem
      ): Promise<void> => {
        setLoading(true);
        const brokerID = convertToMonoBrokerId(brokerItem.BrokerID);

        const res = await prepareUserBroker({ brokerID });
        if (res.Success) {
          window.location.href = `${env.monoAccountURL}/register-external-integration-sec-account/${brokerID}?onetimeToken=${res.OnetimeToken}&provisionalId=${res.ProvisionalID}&callbackUrl=${env.appURL}/userbrokerlink&returnUri=${window.location.href}`;
        } else if (
          !res.Success &&
          (res.Status === '400' || res.Status === '410')
        ) {
          showDataLinkWithdrawalAccountModal();
        } else if (!res.Success && res.Status === '404') {
          showWaitUntilNextUpdateModal();
        }
        setLoading(false);
      };

      const doChangeMono = async (
        brokerItem: GetUserBrokerResultItem
      ): Promise<void> => {
        setLoading(true);

        const res = await prepareUserBroker({
          brokerID: brokerItem.BrokerID,
          account: brokerItem.Account,
        });
        if (res.Success) {
          window.location.href = `${env.monoAccountURL}/register-external-integration-sec-account/${brokerItem.BrokerID}?onetimeToken=${res.OnetimeToken}&monoAccountAggregationId=${brokerItem.Account}&callbackUrl=${env.appURL}/userbrokerlink&returnUri=${window.location.href}`;
        } else if (
          !res.Success &&
          (res.Status === '400' || res.Status === '410')
        ) {
          showDataLinkWithdrawalAccountModal();
        } else if (!res.Success && res.Status === '404') {
          showWaitUntilNextUpdateModal();
        }
        setLoading(false);
      };

      const doDeleteConfirm = (): void => {
        setShowDeleteConfirm(true);
      };

      // formタグまるごと、並行稼働後に破棄
      const brokerElement =
        brokerItem && accountHistory !== null ? (
          <>
            <UserBrokerItem
              group={group}
              brokerItem={brokerItem.current}
              onChangeState={onClickAvailableChange}
              onSyncAccountAsset={onSyncAccountAsset}
              onRefreshMoneytree={onRefreshMoneytree}
              setUserPositions={setUserPositions}
              editable
              positions={positions}
              accountHistory={accountHistory}
              getIsShowDisplayableAssetTypeModalButton={({
                brokerId,
              }): boolean =>
                [
                  consts.BrokerIDss.SBI証券,
                  consts.BrokerIDss.三菱UFJeスマート証券,
                  consts.BrokerIDss.松井証券,
                  consts.BrokerIDss.楽天証券,
                  consts.BrokerIDss.マネックス証券,
                  consts.BrokerIDss.野村證券,
                  consts.BrokerIDss.大和証券,
                  consts.BrokerIDss.SMBC日興証券,
                  consts.BrokerIDss.岡三証券,
                  consts.BrokerIDss.みずほ証券,
                  consts.BrokerIDss.三菱UFJモルガンスタンレー証券,
                  consts.BrokerIDss['JIS&T'],
                  consts.BrokerIDss.NRK,
                  consts.BrokerIDss.SBIベネフィットシステムズ,
                ]
                  .flat()
                  .includes(brokerId)
              }
            />
            <form
              id={`change_${brokerItem.current.BrokerID}_${brokerItem.current.Account}`}
              method="post">
              <input type="hidden" name="onetime_token" />
              <input
                type="hidden"
                name="callback_url"
                value="/securityaccounts"></input>
              <input
                type="hidden"
                name="referer_url"
                value={`/accountdetail/${brokerItem.current.Account}`}
              />
              <input
                type="hidden"
                name="broker_id"
                value={brokerItem.current.BrokerID}
              />
            </form>
          </>
        ) : (
          undefined
        );
      setBrokers(brokerElement);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      brokerItem,
      positions,
      accountHistory,
      setAction,
      setActionPattern,
      groupingState,
    ]
  );

  const doDeleteOKCritter = (brokerItem: GetUserBrokerResultItem): void => {
    setLoading(true);

    deleteAccount(brokerItem.BrokerID, account)
      .then(_ => {
        routing.backRouting(true);
      })
      .catch(_ => {
        setLoading(false);
      });
  };
  const doDeleteOKMono = (brokerItem: GetUserBrokerResultItem): void => {
    setLoading(true);

    deleteAccount(brokerItem.BrokerID, account)
      .then(_ => {
        routing.backRouting(true);
      })
      .catch(_ => {
        setLoading(false);
      });
  };
  const doDeleteOKManual = (brokerItem: GetUserBrokerResultItem): void => {
    deleteManualAsset(brokerItem.Account).then(res => {
      routing.backRouting(true);
    });
  };
  const doDeleteOK = (): void => {
    if (!brokerItem) return;

    switch (brokerItem.current.AggregateSource) {
      case consts.AggregateSourceTypes.テコテック:
        doDeleteOKCritter(brokerItem.current);
        break;
      case consts.AggregateSourceTypes.MONO:
        doDeleteOKMono(brokerItem.current);
        break;
      case consts.AggregateSourceTypes.手入力:
        doDeleteOKManual(brokerItem.current);
        break;
    }

    setShowDeleteConfirm(false);
  };

  const doGoMoneytreeSiteOK = (): void => {
    window.open(
      `${env.moneytreeManagementURL}/connection/${brokerItem?.current.Account}?client_id=${env.moneytreeClientId}`,
      '_blank'
    );
    setShowGoMoneytreeSiteDialog(false);
  };

  const onClickAvailableChange = (item: GetUserBrokerResultItem): void => {
    setItemToUpdate(item);
  };
  const doStateChange = (): void => {
    if (!itemToUpdate || !brokerItem) return;

    // オブジェクトを先に書き換えてしまうとローディングと同時にトグルが変わってしまうので、更新APIの呼び出し後に変える
    // パフォーマンスが許すなら、再度取得APIを呼び出すのも手かも
    const targets = brokerItem.arr
      .filter(x =>
        x.Account === itemToUpdate.Account ? !x.Available : x.Available
      )
      .map(x => x.Account);

    setLoading(true);
    updateAccountAvailable(targets)
      .then(_ => setUserBroker())
      .then(_ => setLoading(false));
    setItemToUpdate(null);
  };

  const onSyncAccountAsset = async (
    userBroker: GetUserBrokerResultItem
  ): Promise<void> => {
    setLoadingModal(true);
    setShowSyncAccountAssetResultModal(true);
    const ret = await syncAccountAsset(userBroker.Account);
    setSyncAccountAssetResult(ret.ResultCode);
    setLoadingModal(false);
  };

  const hideSyncAccountAssetResultModal = () => {
    setShowSyncAccountAssetResultModal(false);

    getPositions().then(ret => {
      ret && setUserPositions(ret.Items);
    });
  };

  if (!isLoaded(groupingState)) return null;

  return (
    <AboutExchangeRateModalProvider>
      <div className="bg-white">
        <div>{broker}</div>

        <AccountDeleteConfirmModal
          delete={doDeleteOK}
          close={(): void => setShowDeleteConfirm(false)}
          targetGoal={
            (group && isCategorized(group) && group.targetGoal) || null
          }
          groupName={(group && group.name) || ''}
          isShow={showDeleteConfirm}
        />

        {showGoMoneytreeSiteDialog && (
          <GoMoneytreeSiteDialog
            onOK={doGoMoneytreeSiteOK}
            onCancel={(): void => setShowGoMoneytreeSiteDialog(false)}
          />
        )}

        {isLoading && (
          <div className="absolute top-0 left-0 h-full w-full bg-gray-800 opacity-75 flex justify-center items-center">
            <Loading
              loading={true}
              circularLoading={true}
              style={{
                backgroundColor: 'transparent',
              }}
            />
          </div>
        )}

        {itemToUpdate && (
          <Modal
            closeOnClickOutside={true}
            onClose={(): void => {
              setItemToUpdate(null);
            }}
            modalStyle={'mx-4'}>
            <div className="px-4 py-8">
              <div className="flex mb-3 justify-center">
                <div className="font-bold">ご確認ください</div>
              </div>
              <div>
                <div>
                  集計対象を変更すると、資産総額が変わる可能性がありますので、変更の際はご注意ください。
                </div>
                <div>資産総額への反映にはお時間がかかる可能性があります。</div>
                <div>
                  <div
                    className="ui-btn mt-4 py-3 cursor-pointer"
                    onClick={doStateChange}>
                    変更する
                  </div>
                  <div
                    className="mt-2 text-center cursor-pointer"
                    onClick={(): void => setItemToUpdate(null)}>
                    キャンセル
                  </div>
                </div>
              </div>
            </div>
          </Modal>
        )}

        <SyncAccountAssetResultModal
          isShowModal={showSyncAccountAssetResultModal}
          hideModal={(): void => hideSyncAccountAssetResultModal()}
          resultCode={syncAccountAssetResult}
          isLoading={isLoadingModal}
        />
        <DataLinkWithdrawalAccountModal
          hideModal={hideDataLinkWithdrawalAccountModal}
          isShowModal={isShowDataLinkWithdrawalAccountModal}
        />
        <WaitUntilNextUpdateModal
          hideModal={hideWaitUntilNextUpdateModal}
          isShowModal={isShowWaitUntilNextUpdateModal}
          errorCode={'101'}
        />
      </div>
    </AboutExchangeRateModalProvider>
  );
};

export default AccountDetail;
